import {
  type AppConfigBase,
  type StorefrontWidget,
  StorefrontWidgetTypeAll,
  type TopMenuLink,
  TopMenuLinkType,
  type TopMenuWidgetType,
  WidgetSingleViewId,
  type WidgetStaticPage,
  type WidgetStaticPageCategory
} from 'ecosystem'
import { hrefFromAppPaths } from 'shared-utils'
import { widgetApi } from '../storefront-api/widgetEndpoint'

export interface TopMenuNavLink {
  id: string
  label: string
  path: string
  isExternal?: boolean
}

export interface TopMenuNavItem {
  id: string
  label: string
  /** `null` when the entry only opens its dropdown. */
  path: string | null
  isExternal?: boolean
  subItems: TopMenuNavLink[]
}

interface ResolveContext {
  pagesById: Map<string, WidgetStaticPage>
  categoriesById: Map<string, WidgetStaticPageCategory>
  paths: AppConfigBase['paths']
}

const pageLabel = (page: WidgetStaticPage) => page.menuLabel?.trim() || page.name

/** `null` when the target no longer exists (deleted page) or the link is incomplete. */
function resolvePath(link: TopMenuLink, { pagesById, paths }: ResolveContext): string | null {
  switch (link.linkType) {
    case TopMenuLinkType.STATIC_PAGE: {
      const page = link.targetId ? pagesById.get(link.targetId) : undefined
      return page?.slug ? `/${page.slug}` : null
    }
    case TopMenuLinkType.PATH:
      return link.pathObj?.slug ? hrefFromAppPaths(link.pathObj, paths) : null
    default:
      return null
  }
}

function resolveSubItems(widget: TopMenuWidgetType, context: ResolveContext): TopMenuNavLink[] {
  return [...(widget.items ?? [])]
    .sort((a, b) => a.order - b.order)
    .flatMap((item): TopMenuNavLink[] => {
      if (item.linkType === TopMenuLinkType.STATIC_PAGE_CATEGORY) {
        const category = item.targetId ? context.categoriesById.get(item.targetId) : undefined
        return (category?.relatedStaticPages ?? []).flatMap((related) => {
          const page = context.pagesById.get(related.id)
          if (!page?.slug) return []
          return [{ id: `${item.id}-${page.id}`, label: pageLabel(page), path: `/${page.slug}` }]
        })
      }

      const path = resolvePath(item, context)
      if (!path) return []

      const page =
        item.linkType === TopMenuLinkType.STATIC_PAGE && item.targetId
          ? context.pagesById.get(item.targetId)
          : undefined
      const label = item.title?.trim() || (page ? pageLabel(page) : '')
      if (!label) return []

      return [{ id: item.id, label, path, isExternal: item.isExternal }]
    })
}

/**
 * The store's top menu, built in SFA from `TOP_MENU_ITEM` widgets in the `TOP_MENU` view.
 * An empty array means the store has no menu configured and keeps its default navigation.
 */
export async function topMenuFactory(paths: AppConfigBase['paths']): Promise<TopMenuNavItem[]> {
  try {
    const menuWidgets = (await widgetApi.getWidgetsByView(
      WidgetSingleViewId.TOP_MENU
    )) as StorefrontWidget<TopMenuWidgetType>[]

    if (!menuWidgets.length) return []

    const links = menuWidgets.flatMap(({ widget }) => [widget, ...(widget.items ?? [])])
    const needsPages = links.some(
      (l) =>
        l.linkType === TopMenuLinkType.STATIC_PAGE ||
        l.linkType === TopMenuLinkType.STATIC_PAGE_CATEGORY
    )
    const needsCategories = links.some((l) => l.linkType === TopMenuLinkType.STATIC_PAGE_CATEGORY)

    const [pageWidgets, categoryWidgets] = (await Promise.all([
      needsPages
        ? widgetApi.getWidgets({
            view: WidgetSingleViewId.STATIC_VIEWS,
            type: StorefrontWidgetTypeAll.WIDGET_STATIC_VIEW
          })
        : [],
      needsCategories
        ? widgetApi.getWidgets({
            view: WidgetSingleViewId.STATIC_VIEWS,
            type: StorefrontWidgetTypeAll.WIDGET_STATIC_VIEW_CATEGORY
          })
        : []
    ])) as [StorefrontWidget<WidgetStaticPage>[], StorefrontWidget<WidgetStaticPageCategory>[]]

    const context: ResolveContext = {
      pagesById: new Map(pageWidgets.map(({ widget }) => [widget.id, widget])),
      categoriesById: new Map(categoryWidgets.map(({ widget }) => [widget.id, widget])),
      paths
    }

    return [...menuWidgets]
      .sort((a, b) => a.order - b.order)
      .flatMap(({ id, widget }): TopMenuNavItem[] => {
        const path = resolvePath(widget, context)
        const subItems = resolveSubItems(widget, context)
        const label = widget.title?.trim()

        // nothing to click and nothing to open
        if (!label || (!path && !subItems.length)) return []

        return [{ id, label, path, isExternal: widget.isExternal, subItems }]
      })
  } catch (error) {
    // the menu must never take the whole layout down; the store falls back to its default nav
    console.error('[topMenuFactory]', error)
    return []
  }
}
